import React, { useRef, useMemo, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface DustParticle {
  angle: number;
  radiusFactor: number;
  size: number;
  speedMultiplier: number;
  offsetY: number;
}

export function ProtostarsIgnition() {
  const dustRef = useRef<DustParticle[]>([]);

  const initDust = () => {
    const list: DustParticle[] = [];
    for (let i = 0; i < 300; i++) {
      list.push({
        angle: Math.random() * Math.PI * 2,
        radiusFactor: 0.1 + Math.random() * 0.9,
        size: 1.0 + Math.random() * 1.5,
        speedMultiplier: 0.6 + Math.random() * 0.8,
        offsetY: (Math.random() - 0.5) * 12
      });
    }
    return list;
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    const cycleTime = 8.0;
    const t = totalTime % cycleTime;

    // Initialize dust particles once
    if (dustRef.current.length === 0) {
      dustRef.current = initDust();
    }

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const dust = dustRef.current;

    // Render loop helper to draw temperature gauge
    const drawGauge = (fillProgress: number, label: string, color: string) => {
      const gx = 45;
      const gy = 100;
      const gHeight = 140;
      const gWidth = 12;

      // Draw gauge border/track
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(gx, gy, gWidth, gHeight);

      // Draw gauge fill
      ctx.fillStyle = color;
      const filledHeight = fillProgress * gHeight;
      ctx.fillRect(gx + 1, gy + gHeight - filledHeight + 1, gWidth - 2, filledHeight - 2);

      // Labels
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('CORE TEMP', gx + gWidth / 2, gy - 26);
      
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '11px sans-serif';
      ctx.fillText(label, gx + gWidth / 2, gy - 10);
    };

    if (t < 2.0) {
      // PHASE 0: Stable gas disk collapse start
      const spiralFactor = Math.pow(1.0 - 0.15 * (t / 2.0), 2);
      const angleOffset = t * 1.5;

      // Draw dust disk
      dust.forEach(p => {
        const currentAngle = p.angle + angleOffset * p.speedMultiplier;
        const rMajor = 180 * p.radiusFactor * spiralFactor;
        const rMinor = 55 * p.radiusFactor * spiralFactor;
        
        const px = cx + Math.cos(currentAngle) * rMajor;
        const py = cy + Math.sin(currentAngle) * rMinor + p.offsetY * spiralFactor;

        ctx.fillStyle = 'rgba(196, 181, 253, 0.55)'; // Light violet dust
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Core: dim red glow
      const coreGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 14);
      coreGlow.addColorStop(0, 'rgba(239, 68, 68, 0.65)'); // Dim red
      coreGlow.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = coreGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fill();

      // State label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Gravitational Collapse: Gas Disk Accumulates', cx, cy - 100);

      // Gauge (0% filled, ~10K)
      drawGauge(0.02, '10,000 °C', '#EF4444');

    } else if (t < 4.0) {
      // PHASE 1: Collapse accelerates, core heats up
      const k = (t - 2.0) / 2.0; // 0 to 1
      const spiralFactor = Math.pow(0.85 * (1.0 - k), 3); // accelerate collapse
      const angleOffset = 3.0 + k * 7.0;

      // Draw dust disk spiraling in rapidly
      dust.forEach(p => {
        const currentAngle = p.angle + angleOffset * p.speedMultiplier;
        const rMajor = 180 * p.radiusFactor * spiralFactor;
        const rMinor = 55 * p.radiusFactor * spiralFactor;

        const px = cx + Math.cos(currentAngle) * rMajor;
        const py = cy + Math.sin(currentAngle) * rMinor + p.offsetY * spiralFactor;

        ctx.fillStyle = `rgba(196, 181, 253, ${0.55 * (1.0 - k)})`;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Growing, intensifying core
      const coreRadius = 12 + k * 10;
      const coreGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius * 1.5);
      coreGlow.addColorStop(0, `rgba(251, 191, 36, ${0.6 + k * 0.4})`); // Yellow core
      coreGlow.addColorStop(0.5, 'rgba(239, 68, 68, 0.5)'); // Red outer
      coreGlow.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = coreGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, coreRadius * 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Labels based on core heat
      let tempLabel = '1M °C';
      let gaugeColor = '#EF4444'; // Red
      if (k > 0.8) {
        tempLabel = '15M °C — IGNITION!';
        gaugeColor = '#FFFFFF'; // White hot
      } else if (k > 0.5) {
        tempLabel = '10M °C';
        gaugeColor = '#FBBF24'; // Yellow
      } else if (k > 0.25) {
        tempLabel = '5M °C';
        gaugeColor = '#F97316'; // Orange
      }

      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Gravity Compresses Core, Temperature Soars', cx, cy - 100);

      drawGauge(k, tempLabel, gaugeColor);

    } else if (t < 4.5) {
      // PHASE 2: IGNITION white flash & expanding shockwave ring
      const k = (t - 4.0) / 0.5; // 0 to 1

      // Draw Star Core in center
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(cx, cy, 22, 0, Math.PI * 2);
      ctx.fill();

      // Shockwave Ring
      const shockRadius = k * Math.max(width, height) * 0.8;
      ctx.strokeStyle = `rgba(255, 255, 255, ${1 - k})`;
      ctx.lineWidth = 3 * (1 - k);
      ctx.beginPath();
      ctx.arc(cx, cy, shockRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Blown away dust particles moving outward & fading
      dust.forEach(p => {
        const rMajor = 180 * p.radiusFactor * (1.0 + k * 1.5);
        const rMinor = 55 * p.radiusFactor * (1.0 + k * 1.5);
        const px = cx + Math.cos(p.angle) * rMajor;
        const py = cy + Math.sin(p.angle) * rMinor;

        ctx.fillStyle = `rgba(255, 255, 255, ${0.4 * (1.0 - k)})`;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Overlay Flash (white fill fading)
      ctx.fillStyle = `rgba(255, 255, 255, ${1 - k})`;
      ctx.fillRect(0, 0, width, height);

      // Phase title
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Star Ignition! Nuclear Fusion Commences', cx, cy - 100);

      drawGauge(1.0, '15M °C — IGNITION!', '#FFFFFF');

    } else if (t < 7.0) {
      // PHASE 3: New star revealed with corona rays
      const k = (t - 4.5) / 2.5; // 0 to 1

      // 8 rotating corona rays
      const angleOffset = totalTime * 0.3;
      ctx.fillStyle = 'rgba(253, 224, 71, 0.3)'; // Soft glowing yellow rays
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const rayAngle = angleOffset + (i * Math.PI / 4);
        const outerX = cx + Math.cos(rayAngle) * 58;
        const outerY = cy + Math.sin(rayAngle) * 58;

        const midAngle1 = rayAngle - Math.PI / 8;
        const midX1 = cx + Math.cos(midAngle1) * 18;
        const midY1 = cy + Math.sin(midAngle1) * 18;

        const midAngle2 = rayAngle + Math.PI / 8;
        const midX2 = cx + Math.cos(midAngle2) * 18;
        const midY2 = cy + Math.sin(midAngle2) * 18;

        if (i === 0) ctx.moveTo(midX1, midY1);
        ctx.lineTo(outerX, outerY);
        ctx.lineTo(midX2, midY2);
      }
      ctx.closePath();
      ctx.fill();

      // Main star core glow
      const starGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 32);
      starGlow.addColorStop(0, '#FFFFFF');
      starGlow.addColorStop(0.3, '#FEF08A'); // Light yellow
      starGlow.addColorStop(0.7, 'rgba(245, 158, 11, 0.4)'); // Orange glow
      starGlow.addColorStop(1, 'rgba(245, 158, 11, 0)');
      ctx.fillStyle = starGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, 32, 0, Math.PI * 2);
      ctx.fill();

      // Core sphere
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(cx, cy, 18, 0, Math.PI * 2);
      ctx.fill();

      // Phase title
      ctx.fillStyle = '#67E8F9'; // Cyan
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('A Protostar is Born!', cx, cy - 100);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '12px sans-serif';
      ctx.fillText('Radiation pressure blows away remaining dust', cx, cy - 80);

      drawGauge(1.0, '15M °C — ACTIVE', '#FDE047');

    } else {
      // PHASE 4: Fade to black, reset
      const k = (t - 7.0) / 1.0; // 0 to 1

      // Draw fading star
      const starOpacity = 1.0 - k;
      ctx.fillStyle = `rgba(255, 255, 255, ${starOpacity})`;
      ctx.beginPath();
      ctx.arc(cx, cy, 18, 0, Math.PI * 2);
      ctx.fill();

      // Black overlay fading in
      ctx.fillStyle = `rgba(15, 23, 42, ${k})`;
      ctx.fillRect(0, 0, width, height);

      drawGauge(1.0 - k, 'RESETTING', `rgba(255, 255, 255, ${1 - k})`);
    }

    // Top-Right Looping Indicator
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('↺ Looping', width - 20, 24);
  }, []);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
